"use client";

import { ChevronDown, Search } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface DropdownOption {
  value: string;
  label: string;
}

export interface MultiSelectDropdownProps {
  label: string;
  options: DropdownOption[];
  selectedValues: string[];
  onSelect: (values: string[]) => void;
  isOpen: boolean;
  isSymbol?: boolean;
  onToggle: () => void;
}

/**
 * Multi-Select Dropdown with Checkboxes Component
 */
export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues = [],
  onSelect,
  isOpen,
  onToggle,
  isSymbol,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const allSelected = useMemo(() => {
    return (
      options.length > 0 &&
      options.every((opt) => selectedValues.includes(opt.value))
    );
  }, [options, selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelect(options.map((opt) => opt.value));
    } else {
      onSelect([]);
    }
  };

  const handleOptionToggle = (value: string, checked: boolean) => {
    if (checked) {
      onSelect([...selectedValues, value]);
    } else {
      onSelect(selectedValues.filter((v) => v !== value));
    }
  };

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) return label;
    if (selectedValues.length === 1) {
      const selected = options.find((opt) => opt.value === selectedValues[0]);
      return selected ? selected.label : `${selectedValues.length} selected`;
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, label]);

  return (
    <div
      className="relative inline-block text-left min-w-full md:min-w-[200px] w-full max-w-xs"
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full px-3 py-2 bg-white dark:bg-[#1E1E2D] border border-gray-300 dark:border-gray-600 text-sm rounded-lg cursor-pointer text-gray-800 dark:text-white transition duration-150 hover:bg-gray-50 dark:hover:bg-[#252538] focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset shadow-sm"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          className={`transform transition-transform ${
            isOpen ? "rotate-180" : "rotate-0"
          } w-3 h-3 text-gray-500 dark:text-gray-400`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-40 mt-1 w-full min-w-full md:min-w-48 origin-top-right rounded-md shadow-xl bg-white dark:bg-[#252538] focus:outline-none max-h-80 flex flex-col"
          role="menu"
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-[#1E1E2D] border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset text-gray-800 dark:text-white"
              />
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto max-h-48">
            {!isSymbol && (
              <label className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#33334e] cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-blue-500 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:bg-gray-700"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <span className="ml-2 font-medium">Select All</span>
              </label>
            )}

            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#33334e] cursor-pointer"
              >
                <div className="flex">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-500 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:bg-gray-700"
                    checked={selectedValues.includes(option.value)}
                    onChange={(e) =>
                      handleOptionToggle(option.value, e.target.checked)
                    }
                  />
                </div>
                <span className="ml-2">{option.label}</span>
              </label>
            ))}

            {filteredOptions.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface InsiderFilterProps {
  persontype: DropdownOption[];
  marketCapType: DropdownOption[];
  transactionType: DropdownOption[];
  symbolType: DropdownOption[];
  setSelectedPersonType: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedTransactionType: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectMarketCap: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectSymbol: React.Dispatch<React.SetStateAction<string[]>>;
  selectedPersonType: string[];
  selectedTransactionType: string[];
  selectMarketCap: string[];
  selectSymbol: string[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const InsiderFilter = (props: InsiderFilterProps) => {
  const {
    persontype,
    marketCapType,
    transactionType,
    selectedPersonType,
    setSelectedPersonType,
    selectedTransactionType,
    setSelectedTransactionType,
    selectMarketCap,
    setSelectMarketCap,
    setSelectSymbol,
    symbolType,
    selectSymbol,
    setCurrentPage,
  } = props;

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [resetCount, setResetCount] = useState(0);

  const toggleDropdown = useCallback((name: string) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  }, []);

  const handleSelectAndResetPage = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    values: string[],
  ) => {
    setter(values);
    setCurrentPage(1);
  };

  return (
    <div className="px-4 py-2">
      <div className="flex flex-wrap justify-between items-center">
        <h1 className="text-2xl font-medium text-gray-800 dark:text-white">
          Transactions
        </h1>
        <div className="">
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex flex-col w-full md:w-auto">
              <span className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Select Stocks
              </span>
              <MultiSelectDropdown
                key={`stocks-${resetCount}`}
                label="Select Stocks"
                options={symbolType}
                isSymbol={true}
                selectedValues={selectSymbol}
                onSelect={(values) =>
                  handleSelectAndResetPage(setSelectSymbol, values)
                }
                isOpen={activeDropdown === "stocks"}
                onToggle={() => toggleDropdown("stocks")}
              />
            </div>
            <div className="flex flex-col w-full md:w-auto">
              <span className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Select category
              </span>
              <MultiSelectDropdown
                key={`category-${resetCount}`}
                label="Select Category"
                options={persontype}
                selectedValues={selectedPersonType}
                onSelect={(values) =>
                  handleSelectAndResetPage(setSelectedPersonType, values)
                }
                isOpen={activeDropdown === "category"}
                onToggle={() => toggleDropdown("category")}
              />
            </div>

            <div className="flex flex-col w-full md:w-auto">
              <span className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Select Transaction Type
              </span>
              <MultiSelectDropdown
                key={`transaction-${resetCount}`}
                label="Select Transaction Type"
                options={transactionType}
                selectedValues={selectedTransactionType}
                onSelect={(values) =>
                  handleSelectAndResetPage(setSelectedTransactionType, values)
                }
                isOpen={activeDropdown === "transactionType"}
                onToggle={() => toggleDropdown("transactionType")}
              />
            </div>
            <div className="flex flex-col w-full md:w-auto">
              <span className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                Select Market Cap
              </span>
              <MultiSelectDropdown
                key={`marketcap-${resetCount}`}
                label="Select Market Cap"
                options={marketCapType}
                selectedValues={selectMarketCap}
                onSelect={(values) =>
                  handleSelectAndResetPage(setSelectMarketCap, values)
                }
                isOpen={activeDropdown === "marketCap"}
                onToggle={() => toggleDropdown("marketCap")}
              />
            </div>
            <div className="flex items-end mb-1">
              <button
                className="cursor-pointer flex items-end space-x-1 px-3 py-1.5 rounded-md text-sm text-white transition-all duration-200 font-figtree hover:opacity-90
                bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] !mt-0 max-sm:px-2"
                onClick={() => {
                  setSelectedTransactionType([]);
                  setSelectMarketCap([]);
                  setSelectedPersonType([]);
                  setSelectSymbol([]);
                  setCurrentPage(1);
                  setResetCount((prev) => prev + 1);
                }}
              >
                Reset Filter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsiderFilter;
